import { DatePipe } from '@angular/common';
import {
  AfterViewInit,
  Component,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatSelect } from '@angular/material/select';
import { ActivatedRoute, Router } from '@angular/router';
import { BciLoaderService } from '@bci-web-core/core';
import { TranslateService } from '@ngx-translate/core';
import { Chart } from 'angular-highcharts';
import dayjs from 'dayjs/esm';
import * as moment from 'moment';
import { Observable, ReplaySubject, Subject, forkJoin } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { CommonService } from 'src/app/services/common.service';
import {
  Constants,
  DateService,
  Highcharts$,
  WarningDialog
} from 'src/app/shared/index';
import { SiteService } from '../../../../services/site.service';
import { DataTagComponent } from '../data-tag/data-tag.component';

@Component({
  selector: 'app-deep-dive',
  templateUrl: './deep-dive.component.html',
  styleUrls: ['./deep-dive.component.scss']
})
export class DeepDiveComponent implements OnInit, AfterViewInit, OnDestroy {
  search: string;
  loadUIRequired = false;
  loadUIDisabled = false;
  parameterUIRequired = false;
  parameterUIDisabled = false;
  form = {
    loadsAndMeters: []
  };
  siteId = ''; // = (commonService.isNotSiteSpecific ? siteService.getSiteID() : commonService.siteId);

  loads = [];
  meters = [];
  dgs = [];
  params = [];
  selectLoadData = [];
  displayUpdateTagAlert = false;
  tagParameters = {
    rid: '',
    siteID: '',
    loadName: '',
    loadRID: '',
    loadID: '',
    flowChannelName: '',
    time: '',
    paramName: '',
    paramValue: '',
    tagType: '',
    tagDescription: '',
    taggedBy: '',
    paramUnit: '',
    paramDisplayValue: '',
    tagValid: '',
    modifiedTime: ''
  };
  criteria = {
    loads: [],
    params: [],
    meters: [],
    startTime: null,
    endTime: null,
    siteid: this.siteId
  };
  isMeter = false;
  displayAddTagAlert = false;
  isData = true;
  alwaysShowCalendars: boolean;
  ranges = {
    Today: [new Date(), new Date()],
    Yesterday: [
      this.dateService.subDay(new Date(), 1),
      this.dateService.subDay(new Date(), 1)
    ],
    'Last 7 days': [dayjs().subtract(6, 'days'), dayjs()]
  };

  datesSelected: any;
  deepDriveParams: any;
  areaChartConfig: any;
  loadingBar: any;

  areaChart: Chart;
  buildings: any;
  formControl = new UntypedFormControl(new Date());
  public loadMultiCtrl: UntypedFormControl = new UntypedFormControl();
  public loadMultiFilterCtrl: UntypedFormControl = new UntypedFormControl();
  public paramMultiCtrl: UntypedFormControl = new UntypedFormControl();
  public paramMultiFilterCtrl: UntypedFormControl = new UntypedFormControl();
  public buildingMultiCtrl: UntypedFormControl = new UntypedFormControl();
  public buildingMultiFilterCtrl: UntypedFormControl = new UntypedFormControl();
  public filteredBuildingsMulti: ReplaySubject<any[]> = new ReplaySubject<
    any[]
  >(1);
  public filteredLoadsMulti: ReplaySubject<any[]> = new ReplaySubject<any[]>(1);
  public filteredParamsMulti: ReplaySubject<any[]> = new ReplaySubject<any[]>(
    1
  );
  protected _onBuildingDestroy = new Subject<void>();
  protected _onLoadDestroy = new Subject<void>();
  protected _onParamDestroy = new Subject<void>();
  @ViewChild('buildingMultiSelect') buildingMultiSelect: MatSelect;
  @ViewChild('loadMultiSelect') loadMultiSelect: MatSelect;
  @ViewChild('paramMultiSelect') paramMultiSelect: MatSelect;

  constructor(
    private dateService: DateService,
    private datePipe: DatePipe,
    private siteService: SiteService,
    private commonService: CommonService,
    public dialog: MatDialog,
    private loader: BciLoaderService,
    private router: Router,
    private activeRoute: ActivatedRoute,
    private translate: TranslateService
  ) {
    this.search = this.translate.instant(Constants.SEARCH);
    this.siteId = this.activeRoute.snapshot.queryParams['siteId'];
    this.form.loadsAndMeters = [];
    this.siteService.getProcessBySiteId(this.siteId).subscribe((data) => {
      this.buildings = data;
      this.initFilterBuilding();
    });
  }

  ngOnInit() {
    const t = this;
    t.areaChartConfig = t.onInitChart();
    t.resetForm();
  }

  ngAfterViewInit() {
    this.setInitialLoad();
    this.setInitialParam();
  }

  ngOnDestroy() {
    if (this._onBuildingDestroy) {
      this._onBuildingDestroy.next();
      this._onBuildingDestroy.complete();
    }

    if (this._onLoadDestroy) {
      this._onLoadDestroy.next();
      this._onLoadDestroy.complete();
    }

    if (this._onParamDestroy) {
      this._onParamDestroy.next();
      this._onParamDestroy.complete();
    }
  }

  initFilterBuilding() {
    // load the initial building list
    if (this.buildings && this.buildings.length > 0) {
      this.filteredBuildingsMulti.next(this.buildings.slice());
    }

    // listen for search field value changes
    this.buildingMultiFilterCtrl.valueChanges
      .pipe(takeUntil(this._onBuildingDestroy))
      .subscribe(() => {
        this.filterBuildingMulti();
      });
  }

  initFilterLoad() {
    // load the initial bank list
    if (this.selectLoadData && this.selectLoadData.length > 0) {
      this.filteredLoadsMulti.next(this.selectLoadData.slice());
    } else {
      this.filteredLoadsMulti.next(this.selectLoadData);
    }

    // listen for search field value changes
    this.loadMultiFilterCtrl.valueChanges
      .pipe(takeUntil(this._onLoadDestroy))
      .subscribe(() => {
        this.filterLoadsMulti();
      });
  }

  initFilterParam() {
    // load the initial bank list
    this.filteredParamsMulti.next(this.deepDriveParams.slice());
    // listen for search field value changes

    this.paramMultiFilterCtrl.valueChanges
      .pipe(takeUntil(this._onParamDestroy))
      .subscribe(() => {
        this.filterParamsMulti();
      });
  }

  protected setInitialBuilding() {
    this.filteredBuildingsMulti
      .pipe(take(1), takeUntil(this._onBuildingDestroy))
      .subscribe(() => {
        this.buildingMultiSelect.compareWith = (a: any, b: any) =>
          a && b && a === b;
      });
  }

  protected setInitialLoad() {
    this.filteredLoadsMulti
      .pipe(take(1), takeUntil(this._onLoadDestroy))
      .subscribe(() => {
        this.loadMultiSelect.compareWith = (a: any, b: any) =>
          a && b && a.rId === b.rId;
      });
  }

  protected setInitialParam() {
    this.filteredParamsMulti
      .pipe(take(1), takeUntil(this._onParamDestroy))
      .subscribe(() => {
        this.paramMultiSelect.compareWith = (a: any, b: any) =>
          a && b && a === b;
      });
  }

  protected filterBuildingMulti() {
    // get the search keyword
    let search = this.buildingMultiFilterCtrl.value;
    if (!search) {
      this.filteredBuildingsMulti.next(this.buildings.slice());
      return;
    } else {
      search = search.toLowerCase();
    }
    // filter the buildings
    this.filteredBuildingsMulti.next(
      this.buildings.filter(
        (building) => building.toLowerCase().indexOf(search) > -1
      )
    );
  }

  protected filterLoadsMulti() {
    if (!this.selectLoadData) {
      return;
    }
    // get the search keyword
    let search = this.loadMultiFilterCtrl.value;
    if (!search) {
      this.filteredLoadsMulti.next(this.selectLoadData.slice());
      return;
    } else {
      search = search.toLowerCase();
    }
    // filter the loads
    this.filteredLoadsMulti.next(
      this.selectLoadData.filter(
        (load) => load.displayText.toLowerCase().indexOf(search) > -1
      )
    );
  }

  protected filterParamsMulti() {
    if (!this.deepDriveParams) {
      return;
    }
    // get the search keyword
    let search = this.paramMultiFilterCtrl.value;
    if (!search) {
      if (this.deepDriveParams && this.deepDriveParams.length > 0) {
        this.filteredParamsMulti.next(this.deepDriveParams.slice());
      }
      return;
    } else {
      search = search.toLowerCase();
    }
    // filter the params
    this.filteredParamsMulti.next(
      this.deepDriveParams.filter(
        (param) => param.text.toLowerCase().indexOf(search) > -1
      )
    );
  }

  initLoadData(processName: any) {
    const t = this;
    let obs: Observable<any>[] = [];
    if (processName.length === 0) {
      t.selectLoadData = [];
      t.initFilterLoad();
    }
    for (let i = 0; i < processName.length; i++) {
      obs.push(t.siteService.getLoadsByProcessName(t.siteId, processName[i]));
    }
    forkJoin(obs).subscribe((loadsData) => {
      t.loads = loadsData;
      t.loadingBar = t.loader.showProgressBar();
      const promise = t.prepareDataForLoadSelect(loadsData);
      promise.then((val) => {
        setTimeout(function () {
          t.loader.hideProgressBar(t.loadingBar);
          t.initFilterLoad();
        }, 1000);
      });
    });
  }

  prepareDataForLoadSelect(loadsData: any) {
    return new Promise((resolve, reject) => {
      const t = this;
      t.selectLoadData = [];
      for (let i = 0; i < loadsData.length; i++) {
        for (let j = 0; j < loadsData[i].length; j++) {
          t.selectLoadData.push(
            t.buildSelectLoadObj(
              loadsData[i][j].rId,
              'load',
              loadsData[i][j].name
            )
          );
        }
        if (i === loadsData.length - 1) {
          resolve(true);
        }
      }
    });
  }

  onInitChart() {
    return {
      chart: {
        defaultSeriesType: 'spline',
        zoomType: 'x',
        height: 600,
        borderWidth: 0.2,
        plotBorderWidth: 0.2
      },

      title: {
        text: ''
      },
      yAxis: {},
      xAxis: {
        title: {
          text: 'Time',
          style: {
            fontWeight: 'bold',
            fontSize: 12
          }
        },
        type: 'datetime',
        labels: {
          style: {
            fontWeight: 'bold',
            fontSize: 12
          }
        },
        minRange: 10 * 1000,
        tickLength: 10,
        startOnTick: Constants.BOOLEAN_TRUE,
        endOnTick: Constants.BOOLEAN_TRUE,
        tickInterval: (3600 * 1000) / (60 * 5),
        pointInterval: (3600 * 1000) / 4,
        dateTimeLabelFormats: {
          hour: '%H:%M'
        }
      },
      plotOptions: {
        series: {
          marker: {
            enabled: Constants.BOOLEAN_TRUE
          },
          type: 'spline',
          turboThreshold: 0,
          threshold: Constants.NULL_VALUE,
          point: {
            events: {
              click: function () {}
            }
          }
        }
      },
      credits: {
        enabled: false
      },
      navigator: {
        enabled: Constants.BOOLEAN_TRUE
      },
      series: [],
      tooltip: {
        shared: true,
        crosshairs: true,
        formatter: function () {
          const dateStr = moment(this.x).format('LLLL');
          let toolTipData =
            '<font style="font-size: 10px; font-weight: bold;">' +
            dateStr +
            '</font><br/>';

          for (let i = 0; i < this.points.length; i++) {
            const myPoint = this.points[i];
            let suffix =
              '<b><span style="color:' +
              myPoint.series.data[myPoint.point.index].color +
              '">\u25CF</span>   ' +
              myPoint.series.name +
              ': ' +
              Highcharts$.numberFormat(
                myPoint.y,
                Constants.DECIMAL_FORMAT.ZERO_DECIMAL,
                '.',
                ','
              ) +
              '</b><br/>';
            if (myPoint.series.name.indexOf('Data Tags') !== -1) {
              suffix =
                '<b><span style="color:' +
                myPoint.series.data[myPoint.point.index].color +
                '">\u25CF</span>  ' +
                myPoint.series.name +
                ' : Present0 </b><br/>';
            }
            toolTipData += suffix;
          }
          return toolTipData;
        }
      }
    };
  }
  /* Click&Navigate Trends */
  generateChart() {
    const t = this;

    if (
      dayjs(this.datesSelected.endDate).diff(
        dayjs(this.datesSelected.startDate),
        'days'
      ) > 10
    ) {
      this.onOpenAlertBox(
        'Note: Data display limited to 10 days. Please select duration not more than 10 days'
      );
    } else {
      t.criteria.startTime = t.transformDate(
        t.datesSelected.startDate.valueOf(),
        Constants.DATE.DATE_FORMAT
      );
      t.criteria.endTime = t.transformDate(
        t.datesSelected.endDate.valueOf(),
        Constants.DATE.DATE_FORMAT
      );
      t.siteService.getDeepDiveData(t.criteria).subscribe((data) => {
        this.getDataAndDrawToChart(data);
      });
    }
  }

  getDataAndDrawToChart(data: any) {
    const t = this;
    const series = Object.keys(data);
    if (series.length == 0) {
      this.isData = false;
    } else {
      this.isData = true;
      t.areaChartConfig.series = [];
      t.areaChartConfig.yAxis = [];
      let voltage = false;
      let current = false;
      let frequency = false;
      let powerfactor = false;
      let realtimepower = false;
      let realtimeenergy = false;
      let registerenergy = false;
      let ambient_temp = false;
      let ambient_pressure = false;
      let CFM = false;
      let fuelConsumption = false;
      let irdSensorReading = false;
      let dataTags = false;
      let right = false;
      let partCounter = false;
      for (let i = 0; i < series.length; i++) {
        let id = '';
        if (series[i].indexOf('voltage') !== -1) {
          id = 'V';
          if (!voltage) {
            t.addYAxis(id, id, right);
            voltage = true;
            right = !right;
          }
        } else if (series[i].indexOf('current') !== -1) {
          id = 'Amperes';
          if (!current) {
            t.addYAxis(id, id, right);
            current = true;
            right = !right;
          }
        } else if (series[i].indexOf('Frequency') !== -1) {
          id = 'Hz';
          if (!frequency) {
            t.addYAxis(id, id, right);
            frequency = true;
            right = !right;
          }
        } else if (series[i].indexOf('powerfactor') !== -1) {
          id = 'Powerfactor';
          if (!powerfactor) {
            t.addYAxis(id, id, right);
            powerfactor = true;
            right = !right;
          }
        } else if (series[i].indexOf('realtimepower') !== -1) {
          id = 'kW';
          if (!realtimepower) {
            t.addYAxis(id, id, right);
            realtimepower = true;
            right = !right;
          }
        } else if (series[i].indexOf('realtimeenergy') !== -1) {
          id = 'kWh';
          if (!realtimeenergy) {
            t.addYAxis(id, id, right);
            realtimeenergy = true;
            right = !right;
          }
        } else if (series[i].indexOf('registerenergy') !== -1) {
          id = 'kWh';
          if (!registerenergy) {
            t.addYAxis(id, id, right);
            registerenergy = true;
            right = !right;
          }
        } else if (series[i].indexOf('ambient_temp') !== -1) {
          id = 'degree C';
          if (!ambient_temp) {
            t.addYAxis(id, id, right);
            ambient_temp = true;
            right = !right;
          }
        } else if (series[i].indexOf('ambient_pressure') !== -1) {
          id = 'psi';
          if (!ambient_pressure) {
            t.addYAxis(id, id, right);
            ambient_pressure = true;
            right = !right;
          }
        } else if (series[i].indexOf('CFM') !== -1) {
          id = 'CFM';
          if (!CFM) {
            t.addYAxis(id, id, right);
            CFM = true;
            right = !right;
          }
        } else if (series[i].indexOf('fuelConsumption') !== -1) {
          id = 'Litres';
          if (!fuelConsumption) {
            t.addYAxis(id, id, right);
            fuelConsumption = true;
            right = !right;
          }
        } else if (series[i].indexOf('IrradiationSensorOutput') !== -1) {
          id = 'Units';
          if (!irdSensorReading) {
            t.addYAxis(id, id, right);
            irdSensorReading = true;
            right = !right;
          }
        } else if (series[i].indexOf('Data Tags') !== -1) {
          id = 'Data Tags';
          if (!dataTags) {
            t.addYAxis(id, id, false);
            dataTags = true;
          }
        } else if (series[i].indexOf('partCounter') !== -1) {
          id = 'Units';
          if (!partCounter) {
            t.addYAxis(id, id, false);
            partCounter = true;
          }
        }
        t.addXAxis(series[i], data[series[i]], id);
      }
      // t.areaChartConfig.plotOptions.series.point.events.click =
      //   t.customDataPointClick;
      t.areaChart = new Chart(t.areaChartConfig);
    }
  }

  customDataPointClick = (event: any) => {
    const point = event.point; // series.name
    if (point.series.name !== 'Data Tags') {
      const tagtxtime = dayjs(point.x).format(
        Constants.MOMENT_DATE_FORMAT.FULL_DATE_FORMAT_1
      );
      const paramName = point.series.name;
      const paramValue = point.y;
      this.setTagDetails(tagtxtime, paramName, paramValue);
      if (!this.isMeter) {
        // Open Modal
        const dialogConfig: MatDialogConfig = {
          // height: '80%',
          width: '70%',
          data: {
            tagParameters: this.tagParameters,
            fromDate: this.criteria.startTime,
            toDate: this.criteria.endTime
          }
        };
        // Open View Existing Tags Accordion
        this.dialog.open(DataTagComponent, dialogConfig);
      } else {
        this.onOpenAlertBox(
          'Data Tagging feature is not available for Mains Meters as of now'
        );
      }
    }
  };

  addYAxis(id, text, opposite) {
    const t = this;
    if (id === 'Data Tags') {
      t.areaChartConfig.yAxis.push({
        id: id,
        title: {
          text: ''
        },
        labels: {
          formatter: function () {
            return '';
          }
        },
        opposite: opposite
      });
    } else {
      t.areaChartConfig.yAxis.push({
        id: id,
        title: {
          text: text,
          style: {
            fontWeight: 'bold',
            fontSize: 12
          }
        },
        opposite: opposite,
        labels: {
          formatter: function () {
            return Highcharts$.numberFormat(
              this.value,
              Constants.DECIMAL_FORMAT.ZERO_DECIMAL,
              '.',
              ','
            );
          },
          style: {
            fontWeight: 'bold',
            fontSize: 12
          }
        }
      });
    }
  }

  addXAxis(name, data, yAxisId) {
    const t = this;
    if (yAxisId === 'Data Tags') {
      t.areaChartConfig.series.push({
        name: name,
        data: data,
        yAxis: yAxisId,
        lineWidth: 0,
        states: {
          hover: {
            lineWidthPlus: 0
          }
        },
        marker: {
          enabled: true,
          symbol: 'url(resources/img/data_tag_redflag.png)'
        }
      });
    } else {
      t.areaChartConfig.series.push({
        name: name,
        data: data,
        yAxis: yAxisId
      });
    }
  }

  resetForm() {
    const t = this;
    this.loadMultiCtrl.setValue('');
    this.buildingMultiCtrl.setValue('');
    t.params = [];
    t.datesSelected = null;

    const d = new Date();
    d.setDate(d.getDate() - 1);
    t.datesSelected = { startDate: d, endDate: d };
    t.criteria = {
      loads: [],
      params: [],
      meters: [],
      startTime: t.transformDate(
        t.datesSelected.startDate.valueOf(),
        Constants.DATE.DATE_FORMAT
      ),
      endTime: t.transformDate(
        t.datesSelected.endDate.valueOf(),
        Constants.DATE.DATE_FORMAT
      ),
      siteid: t.siteId
    };

    if (t.areaChart) {
      t.areaChart.destroy();
    }
  }

  goBack() {}

  changeDateRange(event) {
    if (this.commonService.isLaterThanCurentDate(this.datesSelected.endDate)) {
      this.commonService.showDialogMessage(Constants.FUTURE_DATE_NOT_ALLOW);
      return;
    }
  }
  private onOpenAlertBox = (description: string) => {
    const dialogRef = this.dialog.open(WarningDialog, { width: '50%' });
    dialogRef.componentInstance.confirmMessage = description;
  };

  buildSelectLoadObj(rId, type, displayText) {
    return {
      rId: rId,
      type: type,
      displayText: displayText
    };
  }
  buildParams(event) {
    const t = this;
    t.buildLoadsAndMetersForCriteria();

    const promise = t.buildDeepDriveParams();
    promise.then((val) => {
      t.initFilterParam();
    });
  }

  selectBuildingChange(event: any) {
    this.loadMultiSelect.options.forEach((k) => k.deselect());
    this.initLoadData(event.value);
  }

  buildDeepDriveParams() {
    return new Promise((resolve, reject) => {
      const t = this;
      t.deepDriveParams = [];
      const loadParamArr = t.buildLoadParams();
      if (t.criteria.meters.length > 0) {
        t.deepDriveParams = t.buildLoadParams();
      } else {
        for (let j = 0; j < loadParamArr.length; j++) {
          t.deepDriveParams.push(loadParamArr[j]);
        }
      }
      resolve(true);
    });
  }

  buildLoadParams() {
    return [
      { text: 'current', value: 'current' },
      { text: 'voltage', value: 'voltage' },
      { text: 'realtimepower', value: 'realtimepower' },
      // { text: 'realtimeenergy', value: 'realtimeenergy' },
      { text: 'registerenergy', value: 'registerenergy' }
    ];
  }

  buildLoadsAndMetersForCriteria() {
    const t = this;
    t.criteria.loads = [];
    t.criteria.meters = [];

    for (let i = 0; i < t.form.loadsAndMeters.length; i++) {
      if (t.form.loadsAndMeters[i]) {
        for (let j = 0; j < t.loads.length; j++) {
          for (let k = 0; k < t.loads[j].length; k++) {
            if (t.form.loadsAndMeters[i].displayText == t.loads[j][k].name) {
              if (t.form.loadsAndMeters[i].type === 'load') {
                t.criteria.loads.push(t.loads[j][k]);
              } else {
                t.criteria.meters.push(t.loads[j][k]);
              }
            }
          }
        }
      }
    }
  }

  findByRId(arr, rId) {
    for (let i = 0; i < arr.length; i++) {
      if (arr[i].rId === rId) {
        return arr[i];
      }
    }
  }
  transformDate(date: any, format: string): string {
    return this.datePipe.transform(date, format) || '';
  }
  private setTagDetails = (
    tagtxtime: string,
    paramName: string,
    paramValue: string
  ) => {
    const t = this;
    t.tagParameters.taggedBy = 'admin'; // update later .commonService.username
    t.tagParameters.paramName = paramName;
    t.tagParameters.time = tagtxtime;
    t.tagParameters.siteID = t.siteId;
    t.displayUpdateTagAlert = false;
    t.tagParameters.flowChannelName = '';

    t.tagParameters.tagType = '';
    t.tagParameters.tagDescription = '';
    let unit;

    if (paramName.indexOf('voltage') !== -1) {
      unit = 'V';
    } else if (paramName.indexOf('current') !== -1) {
      unit = 'Amperes';
    } else if (paramName.indexOf('Frequency') !== -1) {
      unit = 'Hz';
    } else if (paramName.indexOf('powerfactor') !== -1) {
      unit = 'Powerfactor';
    } else if (paramName.indexOf('realtimepower') !== -1) {
      unit = 'kW';
    } else if (paramName.indexOf('realtimeenergy') !== -1) {
      unit = 'kWh';
    } else if (paramName.indexOf('registerenergy') !== -1) {
      unit = 'kWh';
    } else if (paramName.indexOf('ambient_temp') !== -1) {
      unit = 'degree C';
    } else if (paramName.indexOf('ambient_pressure') !== -1) {
      unit = 'psi';
    } else if (paramName.indexOf('CFM') !== -1) {
      unit = 'L';
    } else if (paramName.indexOf('fuelConsumption') !== -1) {
      unit = 'litres';
    }

    t.tagParameters.paramValue = paramValue;
    t.tagParameters.paramUnit = unit;
    t.tagParameters.paramDisplayValue = paramValue + ' ' + unit;

    for (let i = 0; i < t.params.length; i++) {
      if (paramName.indexOf(t.params[i].text) !== -1) {
        t.tagParameters.paramName = t.params[i].text;
      }
    }

    for (let i = 0; i < t.selectLoadData.length; i++) {
      if (paramName.indexOf(t.selectLoadData[i].displayText) !== -1) {
        t.tagParameters.loadName = t.selectLoadData[i].displayText;
        t.tagParameters.loadRID = t.selectLoadData[i].rId;
        if (t.selectLoadData[i].type === 'meter') {
          t.isMeter = true;
        } else {
          t.isMeter = false;
        }
      }
    }
  };
}
